import React from "react";
import { Box, Tooltip, useTheme } from "@mui/material";
import { WORKSPACE_AI_PROVIDERS } from "../../helper/aiEnrichment";
import geminiLogo from "../../assets/ai-providers/gemini.png";
import openaiLogo from "../../assets/ai-providers/openai.png";
import anthropicLogo from "../../assets/ai-providers/anthropic.png";

const PROVIDER_LOGOS = {
  gemini: geminiLogo,
  openai: openaiLogo,
  anthropic: anthropicLogo,
};

/** Compact overlapping tiles (reference: stacked / slightly tilted). */
const DEFAULT_CARD_SIZE = 34;
const DEFAULT_LOGO_SIZE = 18;
/** Default / expanded overlap. Compact hover parents override via CSS var. */
const OVERLAP = 1;

const ROTATIONS = {
  gemini: "-7deg",
  openai: "4deg",
  anthropic: "-3deg",
};

/**
 * Overlapping, softly tilted AI provider logo cards.
 *
 * interactive (default) — clickable tiles that select a provider.
 * interactive={false} — decorative deck (no selected state / clicks), for menus.
 * flat — border + fill only (no card shadows); used by the Actions menu.
 * Parent may set --ai-provider-overlap for compact stacking (or spread motion when interactive).
 */
export default function AiProviderIconStack({
  selectedProviderId = null,
  onSelectProvider,
  disabled = false,
  interactive = true,
  cardSize = DEFAULT_CARD_SIZE,
  logoSize = DEFAULT_LOGO_SIZE,
  /** Drop vertical padding — use in dense menus for title-top alignment. */
  dense = false,
  /** Border + fill only — no drop shadows (Actions menu decorative deck). */
  flat = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      data-testid="ai-provider-icon-stack"
      role={interactive ? "group" : "img"}
      aria-label="AI providers"
      aria-hidden={!interactive || undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        pl: dense ? 0 : `${OVERLAP / 2}px`,
        pr: dense ? 0 : `${OVERLAP / 2}px`,
        py: dense ? 0 : "2px",
        minHeight: dense ? cardSize : cardSize + 4,
        lineHeight: 0,
      }}
    >
      {WORKSPACE_AI_PROVIDERS.map((provider, index) => {
        const selected = interactive && provider.id === selectedProviderId;
        const logoSrc = PROVIDER_LOGOS[provider.id];
        const rotation = ROTATIONS[provider.id] || "0deg";
        const cardSx = {
          appearance: "none",
          WebkitAppearance: "none",
          width: cardSize,
          height: cardSize,
          ml: index === 0 ? 0 : `var(--ai-provider-overlap, -${OVERLAP}px)`,
          p: 0,
          borderRadius: dense ? "7px" : "9px",
          border: selected
            ? `1.5px solid ${theme.palette.primary.main}`
            : isDark
              ? "1px solid rgba(255,255,255,0.14)"
              : "1px solid rgba(88,89,91,0.14)",
          backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
          boxShadow: flat
            ? "none"
            : selected
              ? isDark
                ? "0 4px 10px rgba(0,0,0,0.45)"
                : "0 4px 10px rgba(23, 15, 74, 0.14)"
              : isDark
                ? "0 2px 6px rgba(0,0,0,0.4)"
                : "0 2px 6px rgba(88, 89, 91, 0.14)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: interactive && !disabled ? "pointer" : "default",
          zIndex: selected ? WORKSPACE_AI_PROVIDERS.length + 1 : index + 1,
          transform: `rotate(${rotation})`,
          transition: flat
            ? "none"
            : "transform 160ms ease, margin-left 160ms ease, box-shadow 160ms ease, border-color 160ms ease, z-index 0s",
          opacity: disabled ? 0.55 : 1,
          flexShrink: 0,
          ...(interactive && !disabled
            ? {
                "&:hover": {
                  zIndex: WORKSPACE_AI_PROVIDERS.length + 2,
                  transform: `rotate(${rotation}) translateY(-2px)`,
                  boxShadow: flat
                    ? "none"
                    : isDark
                      ? "0 6px 14px rgba(0,0,0,0.5)"
                      : "0 6px 14px rgba(23, 15, 74, 0.16)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                  zIndex: WORKSPACE_AI_PROVIDERS.length + 2,
                },
              }
            : {}),
        };

        const logo = (
          <Box
            component="img"
            src={logoSrc}
            alt=""
            sx={{
              width: logoSize,
              height: logoSize,
              objectFit: "contain",
              display: "block",
              pointerEvents: "none",
              filter:
                isDark && (provider.id === "openai" || provider.id === "anthropic")
                  ? "invert(1) brightness(1.08)"
                  : "none",
            }}
          />
        );

        if (!interactive) {
          return (
            <Box
              key={provider.id}
              role="presentation"
              data-testid={`ai-provider-card-${provider.id}`}
              sx={cardSx}
            >
              {logo}
            </Box>
          );
        }

        return (
          <Tooltip key={provider.id} title={provider.label} enterDelay={400}>
            <Box
              component="button"
              type="button"
              aria-label={`Select ${provider.label}`}
              aria-pressed={selected}
              disabled={disabled}
              data-testid={`ai-provider-card-${provider.id}`}
              onClick={() => {
                if (disabled) return;
                onSelectProvider?.(provider.id);
              }}
              sx={cardSx}
            >
              {logo}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
